'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import { setSession } from '@/utils/tools';
import { Box, Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import ImageNav from './ImageNav';
import styles from './index.module.scss';
import LensProcessNav from './LensProcessNav';
import TextNav from './TextNav';

export interface SideNavProps {
  navData: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  isDarkMode: boolean;
}

// 自定义Tab组件，使用MagicJump包裹a标签
const CustomTab = forwardRef<HTMLElement, any>((props, ref) => {
  const { item, index, children, ...other } = props;
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();

  // 构造跳转目标对象，符合MagicJump的JumpTarget接口
  const jumpTarget = {
    jump_type: item?.content?.jump_type || 3, // 从content中获取jump_type，默认为系统页面类型
    Jump_target: item?.content?.Jump_target || '#', // 从content中获取Jump_target
    Jump_title: item?.content?.nav_name || '',
    product_group: {
      id: item?.content?.product_group?.id,
      path: item?.content?.product_group?.path,
    },
    sku: {
      shop_sku: item?.content?.sku?.shop_sku,
    },
    crowd_recommend: {
      id: item?.content?.crowd_recommend?.id,
    },
  };

  return (
    <MagicJump
      item={jumpTarget}
      onBeforeJump={() => {
        if (
          +item?.content?.jump_type === 1 ||
          `${item?.content?.Jump_target}`?.includes('goods-list')
        ) {
          setSession('zl_goodsListEnterPageScene', '全局导航');
        }
        trackEvent(
          {
            eventName: 'TopNavTab_Click',
            properties: {
              page_name: cnName,
              url_path: window.location.href,
              tab_name: item?.content?.nav_name,
              tab_index: index, // 使用传递过来的index值
            },
          },
          [ProviderType.SENSORS],
        );
      }}
      ref={ref}
      {...other}
    >
      {children}
    </MagicJump>
  );
});

CustomTab.displayName = 'CustomTab';

const SideNav = ({ navData, isDarkMode }: SideNavProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const containerRef = useRef<HTMLDivElement>(null);

  // 委托处理点击Nav内部可跳转元素的事件
  // 旧版是通过统一的Jump组件处理，新版的子元素比较分散，因此用DOM委托处理
  useEffect(() => {
    if (!containerRef) return;
    const handleSubnodeClick = (e: MouseEvent) => {
      let currentTarget = e.target as HTMLElement;
      if (!currentTarget) return;
      do {
        if (currentTarget === containerRef.current) return;
        if (currentTarget.getAttribute('href')) return setShowDropdown(false);
        currentTarget = currentTarget.parentElement;
      } while (currentTarget);
    };
    containerRef.current.addEventListener('click', handleSubnodeClick, true);
    return () => {
      containerRef.current?.removeEventListener('click', handleSubnodeClick, true);
    };
  }, [setShowDropdown]);

  // 清除隐藏定时器
  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // 处理鼠标进入导航项
  const handleTabMouseEnter = useCallback(
    (index: number) => {
      // 避免重复进入判断
      if (index === activeTabIndex) return;
      clearHideTimeout();
      setActiveTabIndex(index);
      setShowDropdown(true);
      const newActiveNavItem = navData[index];
      const hasChildren = newActiveNavItem?.children?.length > 0;
      if (hasChildren) {
        trackEvent(
          {
            eventName: 'TopNavigation_Exp',
            properties: {
              page_name: cnName,
              url_path: window.location.href,
              tab_name: newActiveNavItem.content?.nav_name,
              tab_index: index,
            },
          },
          [ProviderType.SENSORS],
        );
      }
    },
    [activeTabIndex],
  );

  // 处理鼠标离开导航项
  const handleTabMouseLeave = () => {
    // 延迟隐藏，给用户时间移动到下拉菜单
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
      setActiveTabIndex(-1);
    }, 100);
  };

  // 处理弹层鼠标事件
  const handleDropdownMouseEnter = () => {
    clearHideTimeout();
    setShowDropdown(true);
  };

  const handleDropdownMouseLeave = () => {
    clearHideTimeout();
    setShowDropdown(false);
    setActiveTabIndex(-1);
  };

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minWidth: '0',
        // position: 'relative',
        ml: '56px',
        transition: 'all 0.3s',
        '& .MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
          fontFamily: font.br,
          boxSizing: 'border-box',
          height: '44px',
          fontSize: '16px',
          color: isDarkMode ? '#ccc !important' : colors.primaryBlack,
        },
        '& .MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary:hover': {
          fontFamily: font.bb,
          color: isDarkMode ? '#ccc !important' : colors.primaryBlackHover,
          borderBottom: `2px solid ${isDarkMode ? '#ccc !important' : colors.primaryBlackHover}`,
          borderTop: '2px solid transparent',
          letterSpacing: '-0.4px', // 避免加粗后后面的文字抖动
        },
        '& .MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary.Mui-selected': {
          fontWeight: 'normal',
        },
        '& .MuiTabs-indicator': {
          display: 'none!important',
        },
        '& .MuiTabs-list': {
          gap: 'min(2.34375vw, 45px);',
        },
        '& .MuiTabScrollButton-root:first-child': {
          order: 1,
          width: '12px',
          padding: '10px',
          ml: '6px',
          color: isDarkMode ? '#fff' : '#646464',
        },
        '& .MuiTabScrollButton-root:last-child': {
          order: 2,
          width: '12px',
          padding: '6px',
          mr: '12px',
          color: isDarkMode ? '#fff' : '#646464',
        },
        '@media (max-width: 1536px)': {
          '&': {
            ml: '45px',
          },
        },
        '@media (max-width: 1366px)': {
          '&': {
            ml: '32px',
          },
          '& .MuiTabs-list': {
            gap: '24px',
          },
        },
        '@media (max-width: 1024px)': {
          '&': {
            display: 'none',
          },
        },
      }}
      onMouseMove={(e) => {
        // 获取鼠标所在的Tab
        const target = e.target as HTMLElement;
        const tabElement = target.closest('[role="tab"]');
        if (tabElement) {
          const tabs = Array.from(tabElement.parentElement?.children || []);
          const tabIndex = tabs.indexOf(tabElement);
          if (tabIndex >= 0 && tabIndex < navData.length) {
            handleTabMouseEnter(tabIndex);
          }
        }
      }}
      onMouseLeave={handleTabMouseLeave}
    >
      {/* 直接使用MUI Tabs组件，而不是封装的Tabs组件 */}
      <MuiTabs
        value={false} // 不设置选中状态，因为这是导航Tab
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{
          style: { display: 'none' },
        }}
        sx={{
          '& .MuiTab-root': {
            paddingLeft: '0',
            paddingRight: '0',
          },
        }}
      >
        {navData.map((item, index) => (
          <MuiTab
            key={item.id.toString()}
            label={item.content?.nav_name}
            component={CustomTab}
            item={item} // 传递导航项数据给CustomTab
            index={index} // 传递index值给CustomTab
            sx={{
              overflow: 'unset',
            }}
          />
        ))}
      </MuiTabs>
      {/* 预渲染所有的DropdownContent用于SEO，根据activeTabIndex控制显示/隐藏 */}
      {navData.map((navItem, index) => {
        if (!navItem.children || navItem.children.length === 0) return null;

        const isActive = showDropdown && activeTabIndex === index;

        // 过滤不同模板类型的导航项
        const textNavItems = navItem.children.filter((item) =>
          [2, 4].includes(item.template_id || 0),
        );
        const imageNavItems = navItem.children.filter((item) => item.template_id === 3);

        return (
          <div
            key={`dropdown-${navItem.id}`}
            className={`absolute left-[0] top-[60px] z-[202] w-screen ${isDarkMode ? 'bg-[#131415]' : 'bg-white'} flex justify-between items-start px-[48px] box-border shadow-md`}
            style={{
              boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
              display: isActive ? 'flex' : 'none',
            }}
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
          >
            {/* 左侧：TextNav 和 LensProcessNav */}
            <div className="flex-shrink-0 flex gap-[40px] xl:gap-[100px]">
              {textNavItems.map((item, idx) => {
                if (item.template_id === 2) {
                  return (
                    <div key={`${item.id}-${item.template_id}`} className="flex-shrink-0">
                      <TextNav
                        item={item}
                        firstLevelIndex={index}
                        firstLevelTitle={navItem.content?.nav_name || ''}
                        secondLevelIndex={idx}
                        isLast={idx === textNavItems.length - 1}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  );
                }
                if (item.template_id === 4) {
                  return (
                    <div key={`${item.id}-${item.template_id}`} className="flex-shrink-0">
                      <LensProcessNav isDarkMode={isDarkMode} />
                    </div>
                  );
                }
                return null;
              })}
            </div>

            {/* 右侧：ImageNav */}
            <div className={`flex-shrink-1 flex ${styles.dropdownRightContainer}`}>
              {imageNavItems.map((item) => (
                <ImageNav
                  key={`${item.id}-${item.template_id}`}
                  item={item}
                  firstLevelIndex={index}
                  firstLevelTitle={navItem.content?.nav_name || ''}
                  isDarkMode={isDarkMode}
                />
              ))}
            </div>
          </div>
        );
      })}
    </Box>
  );
};

export default SideNav;
